import React from "react";

/**
 * Tiny SVG sparkline with optional shaded optimal band.
 * - series: [{date, value}]
 * - optimalLow, optimalHigh: numeric bounds for the shaded zone
 * - status: "optimal" | "borderline" | "attention" (drives line color)
 */
export default function Sparkline({ series = [], optimalLow, optimalHigh, status, width = 180, height = 48 }) {
  if (!series || series.length === 0) {
    return (
      <div data-testid="sparkline-empty" style={{ width, height }} className="flex items-center justify-center text-[10px] text-[#5C6359]/60">
        no series
      </div>
    );
  }

  const padX = 4;
  const padY = 4;
  const w = width - padX * 2;
  const h = height - padY * 2;

  const values = series.map((p) => p.value);
  let minV = Math.min(...values);
  let maxV = Math.max(...values);

  // Widen the y-range to include the optimal band if present
  if (typeof optimalLow === "number") minV = Math.min(minV, optimalLow);
  if (typeof optimalHigh === "number") maxV = Math.max(maxV, optimalHigh);

  // Add 10% padding above/below for breathing room
  const range = Math.max(maxV - minV, 1);
  const yMin = minV - range * 0.1;
  const yMax = maxV + range * 0.1;
  const ySpan = yMax - yMin || 1;

  const xFor = (i) => padX + (series.length === 1 ? w / 2 : (i / (series.length - 1)) * w);
  const yFor = (v) => padY + h - ((v - yMin) / ySpan) * h;

  const path = series
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xFor(i).toFixed(2)} ${yFor(p.value).toFixed(2)}`)
    .join(" ");

  const lineColor =
    status === "optimal" ? "#2A3B2C" : status === "attention" ? "#8F3A3A" : "#C05C42";

  // Optimal band (shaded rectangle across full width)
  const showBand =
    typeof optimalLow === "number" && typeof optimalHigh === "number" && optimalHigh > optimalLow;
  const bandY1 = showBand ? yFor(optimalHigh) : 0;
  const bandY2 = showBand ? yFor(optimalLow) : 0;

  return (
    <svg
      data-testid="sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      {showBand && (
        <rect
          x={padX}
          y={bandY1}
          width={w}
          height={Math.max(bandY2 - bandY1, 1)}
          fill="#2A3B2C"
          fillOpacity="0.12"
        />
      )}
      <path d={path} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {series.length > 0 && (
        <circle
          cx={xFor(series.length - 1)}
          cy={yFor(series[series.length - 1].value)}
          r="2.4"
          fill={lineColor}
        />
      )}
    </svg>
  );
}
